import { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useViewScope } from '../view-state-atoms'
import { useViewContext } from '../view-context'

interface ViewLeaveGuardOptions {
  /** Any unsent edit — holds back automatic navigation off this entry. */
  hasEdits: boolean
  /** An edit that leaving would lose — user navigation asks first. */
  needsConfirm: boolean
}

/**
 * Registers the mounted view's leave guard: while it has edits, automatic
 * navigation leaves this entry alone, and while one needs confirming, any user
 * navigation off it is deferred into the scope's `pendingLeave` for the view's
 * confirmation to resolve. Registration is keyed to the entry, so a guard left
 * behind by a view still playing its exit (AnimatePresence keeps it mounted
 * past cleanup) is inert — its key no longer matches the top entry. A
 * navigation retried while the confirmation is open simply replaces the
 * pending move: latest intent wins.
 */
export function useViewLeaveGuard({ hasEdits, needsConfirm }: ViewLeaveGuardOptions) {
  const { entryKey } = useViewContext()
  const { navigationGuard, pendingLeave } = useViewScope()
  const setGuard = useSetAtom(navigationGuard)
  const setPendingLeave = useSetAtom(pendingLeave)

  useEffect(() => {
    if (!hasEdits && !needsConfirm) return
    setGuard({ entryKey, confirm: needsConfirm ? (proceed) => setPendingLeave(() => proceed) : null })
    return () => {
      setGuard((guard) => (guard?.entryKey === entryKey ? null : guard))
      // Edits resolved elsewhere (footer save, discard) close the confirmation
      // through here; its own resolutions clear it before this runs.
      setPendingLeave(null)
    }
  }, [hasEdits, needsConfirm, entryKey, setGuard, setPendingLeave])
}
